// Wrapper Google Maps: mappa base, editing marker, percorso (Directions),
// autocomplete, import/export JSON. Richiede l'API caricata (vedi requireGoogleMaps).
export default class MapManager {

    constructor(mapElement, options = {}) {

        const {
            infoElement = null,
            exportElement = null,
            mapId = null,
            travelMode = 'DRIVING',
            onError = (message) => alert(message)
        } = options;

        this.mapElement = mapElement;
        this.infoElement = infoElement;
        this.exportElement = exportElement;
        this.mapId = mapId;
        this.travelMode = travelMode;
        this.onError = onError;

        this.map = null;
        this.directionsService = null;
        this.directionsRenderer = null;
        this.autocomplete = null;
        this.mapClickListener = null;
        this.editingEnabled = false;
        this.routeEnabled = false;
        this.advancedMarkers = [];

        this.inputElement = null;

        this.waypoints = [];
        this.markers = [];
        this.mapType = 'hybrid';
        this.labelsVisible = false;

    }

    // Risolve la stringa travelMode ('DRIVING' | 'BICYCLING' | 'WALKING' | 'TRANSIT')
    getTravelMode() {
        return google.maps.TravelMode[this.travelMode] ?? google.maps.TravelMode.DRIVING;
    }

    // Inizializza solo la mappa base, senza editing o percorso
    initBaseMap(options = {}) {

        const mapOptions = {
            center: { lat: 20, lng: 0 },
            zoom: 2,
            mapTypeId: this.mapType,
            mapTypeControl: false,
            zoomControl: true,
            streetViewControl: false,
            fullscreenControl: false,
            ...options
        };

        if (this.mapId) {
            mapOptions.mapId = this.mapId;
        }

        this.map = new google.maps.Map(this.mapElement, mapOptions);
        this.updateMapStyle();

        return this.map;

    }

    initMap() {

        this.initBaseMap();

    }

    // Abilita l'editing: click sulla mappa aggiunge un marker trascinabile
    enableEditing() {

        if (!this.map) { this.initBaseMap(); }

        this.editingEnabled = true;

        if (this.mapClickListener) { this.mapClickListener.remove(); }

        this.mapClickListener = this.map.addListener("click", (e) => this.addChangeableMarker(e.latLng));

    }

    disableEditing() {

        if (this.mapClickListener) {
            this.mapClickListener.remove();
            this.mapClickListener = null;
        }

        this.editingEnabled = false;

    }

    // Abilita il rendering del percorso (Directions API)
    enableRoute(options = {}) {

        const {
            suppressMarkers = true,
            draggable = false,
            preserveViewport = false,
            recalc = true
        } = options;

        if (!this.map) {
            this.initBaseMap();
        }

        if (!this.directionsService) {
            this.directionsService = new google.maps.DirectionsService();
        }

        if (!this.directionsRenderer) {
            this.directionsRenderer = new google.maps.DirectionsRenderer({
                map: this.map,
                suppressMarkers,
                draggable,
                preserveViewport
            });
        } else {
            this.directionsRenderer.setMap(this.map);
            this.directionsRenderer.setOptions({
                suppressMarkers,
                draggable,
                preserveViewport
            });
        }

        this.routeEnabled = true;

        if (recalc && this.waypoints.length >= 2) {
            this.calcRoute();
        }

    }

    disableRoute() {

        this.routeEnabled = false;

        if (this.directionsRenderer) {
            this.directionsRenderer.set('directions', null);
            this.directionsRenderer.setMap(null);
        }

    }

    // Autocomplete Places su un input: il luogo scelto diventa un marker
    enableAutocomplete(inputElement) {

        this.inputElement = inputElement;
        this.autocomplete = new google.maps.places.Autocomplete(this.inputElement);
        this.autocomplete.bindTo("bounds", this.map);

        this.autocomplete.addListener("place_changed", () => {

            const place = this.autocomplete.getPlace();

            if (!place.geometry || !place.geometry.location) {
                this.onError("Luogo non trovato");
                return;
            }

            this.inputElement.value = '';
            this.map.panTo(place.geometry.location);
            this.map.setZoom(15);
            this.addChangeableMarker(place.geometry.location);

        });

    }

    // Mappa in sola lettura con un percorso esistente (array di {lat, lng})
    initReadOnlyMap(percorso) {

        if (!Array.isArray(percorso) || percorso.length < 2) {
            console.warn("Percorso non valido o troppo corto.");
            return;
        }

        this.initBaseMap({
            center: percorso[0],
            zoom: 14,
            disableDefaultUI: true,
            mapTypeId: this.mapType
        });

        this.disableEditing();
        this.enableRoute({ preserveViewport: true, recalc: false });

        const origin = percorso[0];
        const destination = percorso[percorso.length - 1];
        const via = percorso.slice(1, -1).map(p => ({ location: p, stopover: true }));

        this.directionsService.route({
            origin,
            destination,
            waypoints: via,
            travelMode: this.getTravelMode()
        }, (response, status) => {

            if (status === "OK") {

                this.directionsRenderer.setDirections(response);

                const bounds = new google.maps.LatLngBounds();
                response.routes[0].overview_path.forEach(point => bounds.extend(point));
                this.map.fitBounds(bounds);

                if (google.maps.marker && google.maps.marker.AdvancedMarkerElement && google.maps.marker.PinElement) {

                    const originPin = new google.maps.marker.PinElement({ glyphText: "A" });
                    new google.maps.marker.AdvancedMarkerElement({
                        position: origin,
                        map: this.map,
                        content: originPin.element,
                        title: "A"
                    });

                    const destinationPin = new google.maps.marker.PinElement({ glyphText: "B" });
                    new google.maps.marker.AdvancedMarkerElement({
                        position: destination,
                        map: this.map,
                        content: destinationPin.element,
                        title: "B"
                    });

                } else {

                    new google.maps.Marker({
                        position: origin,
                        map: this.map,
                        label: "A"
                    });

                    new google.maps.Marker({
                        position: destination,
                        map: this.map,
                        label: "B"
                    });

                }

            } else {
                this.onError("Errore nel calcolo del percorso: " + status);
            }

        });

    }

    // type: 'roadmap' | 'satellite' | 'hybrid' | 'terrain'
    setMapType(type) {

        this.mapType = type;
        this.updateMapStyle();

    }

    toggleLabels() {

        this.labelsVisible = !this.labelsVisible;
        this.updateMapStyle();

    }

    updateMapStyle() {

        this.map.setMapTypeId(this.mapType);

        // Con mapId lo stile arriva dal cloud styling: setOptions({ styles }) verrebbe ignorato con warning
        if (this.mapId) return;

        if (this.labelsVisible) {
            this.map.setOptions({ styles: null });
        } else {
            this.map.setOptions({
                styles: [{ featureType: "all", elementType: "labels", stylers: [{ visibility: "off" }] }]
            });
        }

    }

    // Adatta lo zoom per includere percorso e marker
    fitBounds(options = {}) {

        if (!this.map) return;

        const {
            includeRoute = true,
            includeMarkers = true,
            includeAdvancedMarkers = true,
            extraPoints = [],
            padding = 40,
            minZoom = null,
            maxZoom = null
        } = options;

        const bounds = new google.maps.LatLngBounds();
        let hasPoints = false;

        const toLatLng = (point) => {
            if (!point) return null;
            if (typeof point.lat === "function" && typeof point.lng === "function") return point;
            if (typeof point.getPosition === "function") return point.getPosition();
            if (point.position) {
                const pos = point.position;
                if (typeof pos.lat === "function" && typeof pos.lng === "function") return pos;
                if (typeof pos.lat === "number" && typeof pos.lng === "number") {
                    return new google.maps.LatLng(pos.lat, pos.lng);
                }
            }
            if (point.location) return toLatLng(point.location);
            if (typeof point.lat === "number" && typeof point.lng === "number") {
                return new google.maps.LatLng(point.lat, point.lng);
            }
            return null;
        };

        const extendWith = (point) => {
            const latLng = toLatLng(point);
            if (!latLng) return;
            bounds.extend(latLng);
            hasPoints = true;
        };

        if (includeRoute && this.directionsRenderer && this.directionsRenderer.getDirections) {
            const directions = this.directionsRenderer.getDirections();
            const route = directions?.routes?.[0];
            if (route?.overview_path?.length) {
                route.overview_path.forEach(p => extendWith(p));
            } else if (this.waypoints.length) {
                this.waypoints.forEach(wp => extendWith(wp.location));
            }
        } else if (includeRoute && this.waypoints.length) {
            this.waypoints.forEach(wp => extendWith(wp.location));
        }

        if (includeMarkers) {
            this.markers.forEach(marker => extendWith(marker));
        }

        if (includeAdvancedMarkers) {
            this.advancedMarkers.forEach(marker => extendWith(marker));
        }

        if (Array.isArray(extraPoints)) {
            extraPoints.forEach(point => extendWith(point));
        }

        if (!hasPoints) return;

        this.map.fitBounds(bounds, padding);

        if (minZoom !== null || maxZoom !== null) {
            const listener = this.map.addListener("idle", () => {
                const zoom = this.map.getZoom();
                if (minZoom !== null && zoom < minZoom) this.map.setZoom(minZoom);
                if (maxZoom !== null && zoom > maxZoom) this.map.setZoom(maxZoom);
                listener.remove();
            });
        }

    }

    // customContent: (properties) => HTMLElement, es. il global gmapMarkerConstructor
    addAdvancedMarker(lat, lng, title, properties, customContent) {

        const content = typeof customContent === "function" ? customContent(properties) : null;

        if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
            const marker = new google.maps.marker.AdvancedMarkerElement({
                map: this.map,
                content: content,
                position: new google.maps.LatLng(lat, lng),
                title: title,
            });
            this.advancedMarkers.push(marker);
            return marker;
        }

        console.warn("AdvancedMarkerElement non disponibile, uso Marker standard.");
        const fallbackMarker = new google.maps.Marker({
            position: new google.maps.LatLng(lat, lng),
            map: this.map,
            title: title
        });
        this.advancedMarkers.push(fallbackMarker);
        return fallbackMarker;

    }

    addMarker(latLng, label) {

        const marker = new google.maps.Marker({
            position: latLng,
            map: this.map,
            label: label
        });

        return marker;

    }

    // Marker trascinabile: dragend aggiorna il percorso, doppio click lo rimuove
    addChangeableMarker(latLng) {

        const label = (this.markers.length + 1).toString();
        const marker = this.addMarker(latLng, label);

        marker.setDraggable(true);

        marker.addListener("dragend", () => {
            this.updateWaypoints();
            if (this.routeEnabled) this.calcRoute();
        });

        marker.addListener("dblclick", () => {
            marker.setMap(null);
            this.markers = this.markers.filter(m => m !== marker);
            this.updateWaypoints();
            if (this.routeEnabled) this.calcRoute();
        });

        this.markers.push(marker);
        this.updateWaypoints();
        if (this.routeEnabled && this.markers.length >= 2) this.calcRoute();

    }

    updateWaypoints() {

        this.waypoints = this.markers.map(m => ({ location: m.getPosition(), stopover: true }));
        this.markers.forEach((m, i) => m.setLabel((i + 1).toString()));
        this.saveRoute();

    }

    // Calcola il percorso tra i marker con le Directions API e lo disegna
    calcRoute() {

        if (!this.routeEnabled || !this.directionsService || !this.directionsRenderer) return;
        if (this.waypoints.length < 2) return;

        const origin = this.waypoints[0].location;
        const destination = this.waypoints[this.waypoints.length - 1].location;
        const via = this.waypoints.slice(1, -1);

        this.directionsService.route({
            origin,
            destination,
            waypoints: via,
            travelMode: this.getTravelMode()
        }, (response, status) => {
            if (status === "OK") {
                this.directionsRenderer.setDirections(response);
                this.showRouteInfo(response.routes[0].legs);
                const bounds = new google.maps.LatLngBounds();
                response.routes[0].overview_path.forEach(p => bounds.extend(p));
                this.map.fitBounds(bounds);
            } else {
                this.onError("Errore nel calcolo del percorso: " + status);
            }
        });

    }

    showRouteInfo(legs) {

        let totalDistance = 0, totalDuration = 0;

        legs.forEach(leg => {
            totalDistance += leg.distance.value;
            totalDuration += leg.duration.value;
        });

        if (this.infoElement) {
            this.infoElement.innerHTML = `<strong>Distanza:</strong> ${(totalDistance / 1000).toFixed(2)} km<br><strong>Tempo stimato:</strong> ${this.formatTime(totalDuration)}`;
        }

    }

    // Secondi -> "2h 10min" oppure "15min"
    formatTime(seconds) {

        const mins = Math.floor(seconds / 60);
        const hrs = Math.floor(mins / 60);
        const remMins = mins % 60;

        return hrs > 0 ? `${hrs}h ${remMins}min` : `${remMins}min`;

    }

    resetMap() {

        this.markers.forEach(m => m.setMap(null));
        this.markers = [];
        this.waypoints = [];

        if (this.directionsRenderer) this.directionsRenderer.set('directions', null);
        if (this.infoElement) this.infoElement.innerHTML = '';

    }

    // Serializza i marker come JSON [{lat, lng}] nell'exportElement
    saveRoute() {

        const coords = this.markers.map(m => ({ lat: m.getPosition().lat(), lng: m.getPosition().lng() }));

        if (this.exportElement) this.exportElement.value = JSON.stringify(coords, null, 2);

    }

    loadRouteFromJSON(json) {

        this.resetMap();

        const coords = JSON.parse(json);

        coords.forEach(coord => this.addChangeableMarker(new google.maps.LatLng(coord.lat, coord.lng)));

    }

}
